import React from 'react';

const UsingKeyboardContext = React.createContext<React.MutableRefObject<boolean | null>>(
    React.createRef()
);

/**
 * Context provider for the UsingKeyboardContext. Handles setting up the appropriate
 * listeners etc.
 */
export function UsingKeyboardContextProvider(props: { children: React.ReactNode }) {
    const { children } = props;
    const ref = React.useRef<boolean>(false);

    React.useEffect(() => {
        // when a key is pressed, set usingKeyboard to true and listen for the next mouse event.
        const onKeyDown = () => {
            ref.current = true;

            document.removeEventListener('keydown', onKeyDown);
            document.addEventListener('mousedown', onMouseDown, { capture: true });
        };

        // when the mouse/touch is pressed, set usingKeyboard to false and listen for the key event.
        const onMouseDown = () => {
            ref.current = false;

            document.removeEventListener('mousedown', onMouseDown);
            document.addEventListener('keydown', onKeyDown, { capture: true });
        };

        document.addEventListener('mousedown', onMouseDown, { capture: true });
        document.addEventListener('keydown', onKeyDown, { capture: true });

        return () => {
            document.removeEventListener('keydown', onKeyDown);
            document.removeEventListener('mousedown', onMouseDown);
        };
    }, []);

    return <UsingKeyboardContext.Provider value={ref}>{children}</UsingKeyboardContext.Provider>;
}

/**
 * Returns a stable ref containing the current value of the usingKeyboardAtom.
 */
export function useUsingKeyboard(): React.RefObject<boolean> {
    return React.useContext(UsingKeyboardContext);
}
